import { useCallback, useEffect } from "react"
import { useAuth as useClerkAuth } from "@clerk/react"
import { create } from "zustand"
import { apiClient } from "@/services/api.client"
import { useActiveOrgStore } from "../org.store"

export interface MeOrganization {
  id: string
  name: string
  kind: string
  role: string
}

export interface MeMetric {
  metric: string
  limit: number | null
  used: number
  reserved: number
  window: string
  resets_at: string | null
}

/** guest, exempt (staff), free_month (first 30 days), free, plus, pro, grace (a renewal failed, 3 days to pay). */
export type MePlanState = "guest" | "exempt" | "free_month" | "free" | "plus" | "pro" | "grace"

export interface MeRenewal {
  status: string | null
  last_charged_at: string | null
  last_amount_minor: number | null
  next_charge_at: string | null
  /** Where to pay a failed renewal; set while the plan is in grace. */
  pay_url: string | null
}

/** One add-on bought, when /me lists them (Pro only). */
export interface MeAddonPurchase {
  sku: string
  quantity: number
  price_minor: number
  purchased_at: string | null
}

export interface MeEntitlements {
  subscribed: boolean
  exempt: boolean
  plan: string | null
  subscription: { status: string; current_end: string | null; cancel_at_cycle_end: boolean } | null
  metrics: MeMetric[]
  plan_state?: MePlanState | null
  plan_code?: string | null
  /** When the free first month ends (ISO); null once it has. */
  first_month_ends_at?: string | null
  /** The last day a failed renewal can still be paid before the plan drops to Free. */
  grace_ends_at?: string | null
  renewal?: MeRenewal | null
  /** Add-ons bought, newest first, if the API reports them. */
  addon_purchases?: MeAddonPurchase[] | null
}

export interface Me {
  user_id: string
  email: string
  display_name: string
  role: string
  organization_id: string
  organizations: MeOrganization[]
  billing_enabled: boolean
  /** When the account was made (ISO), if the gateway reports it; feeds the `signed_up_at` person property. */
  created_at?: string | null
  /** Allowances from the gateway; null when quotas are off or unavailable. */
  entitlements?: MeEntitlements | null
  /** The largest upload this plan accepts, in bytes (top level of /me, not under entitlements). */
  max_file_bytes?: number | null
}

interface MeState {
  me: Me | null
  loading: boolean
  error: Error | null
  reset: () => void
}

export const useMeStore = create<MeState>((set) => ({
  me: null,
  loading: false,
  error: null,
  reset: () => {
    generation++
    inflight = null
    set({ me: null, loading: false, error: null })
  },
}))

let inflight: Promise<void> | null = null
/** Bumped on reset (sign-out, user switch) so a late /me response cannot repopulate the store. */
let generation = 0

/** Fetch /me once; concurrent callers share the request. */
export function loadMe(force = false): Promise<void> {
  const state = useMeStore.getState()
  if (inflight) return inflight
  if (state.me && !force) return Promise.resolve()
  const gen = generation
  useMeStore.setState({ loading: true, error: null })
  const run = apiClient
    .get<Me>("/api/v1/me")
    .then((me) => {
      if (gen !== generation) return
      const org = useActiveOrgStore.getState()
      if (!org.activeOrgId || !me.organizations.some((o) => o.id === org.activeOrgId)) {
        org.setActiveOrgId(me.organization_id)
      }
      useMeStore.setState({ me, loading: false, error: null })
    })
    .catch((err: unknown) => {
      if (gen !== generation) return
      useMeStore.setState({ loading: false, error: err instanceof Error ? err : new Error(String(err)) })
    })
    .finally(() => {
      if (inflight === run) inflight = null
    })
  inflight = run
  return run
}

export function useCurrentUser(): {
  me: Me | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
} {
  const { isLoaded, isSignedIn } = useClerkAuth()
  const me = useMeStore((s) => s.me)
  const error = useMeStore((s) => s.error)
  const loading = useMeStore((s) => s.loading)

  useEffect(() => {
    if (isLoaded && isSignedIn && !me && !error) void loadMe()
  }, [isLoaded, isSignedIn, me, error])

  const refetch = useCallback(() => {
    useMeStore.setState({ error: null })
    void loadMe(true)
  }, [])

  const signedOut = isLoaded && !isSignedIn
  if (signedOut) return { me: null, isLoading: false, error: null, refetch }
  // A refresh with an account already loaded runs in the background: it must not read as
  // loading, or RequireAuth swaps the page for its spinner and remounts it on every window focus.
  return { me, isLoading: !isLoaded || (!me && (!error || loading)), error, refetch }
}
